import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Btn, FormField, toast } from '../components/UI';
import { LogIn, AlertCircle, Sun, Moon, UserPlus, ArrowLeft, CheckCircle, KeyRound, Mail, ShieldCheck, Building2, Sparkles, Eye, EyeOff, CalendarCheck, Ambulance, Lock } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import api from '../utils/api';
import { validatePassword, validateEmail } from '../utils/validators';
import Turnstile, { TURNSTILE_ENABLED } from '../components/Turnstile';
import BrandLogo from '../components/BrandLogo';

const GOOGLE_ENABLED = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;
const REMEMBER_KEY = 'mc_remember_email';

function readRememberedEmail() {
  try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
}
function writeRememberedEmail(value) {
  try { value ? localStorage.setItem(REMEMBER_KEY, value) : localStorage.removeItem(REMEMBER_KEY); } catch { /* storage bloqueado */ }
}

function ErrorBox({ children, style }) {
  if (!children) return null;
  return (
    <div role="alert" style={{ padding: '10px 14px', background: 'var(--warn-dim)', borderRadius: 8, color: 'var(--warn)', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, ...style }}>
      <AlertCircle size={16} style={{ flexShrink: 0 }} />{children}
    </div>
  );
}

function ThemeToggle({ theme, onToggle }) {
  const label = theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro';
  return (
    <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
      <button type="button" onClick={onToggle} aria-label={label} title={label} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 8, borderRadius: 8 }}>
        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>
    </div>
  );
}

// Input de contraseña con mostrar/ocultar y aviso de Bloq Mayús.
function PasswordInput({ value, onChange, id, autoComplete = 'current-password', ...rest }) {
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const checkCaps = (e) => setCapsLock(!!e.getModifierState?.('CapsLock'));
  return (
    <>
      <div className="auth-input-wrap">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          onKeyUp={checkCaps}
          onKeyDown={checkCaps}
          onBlur={() => setCapsLock(false)}
          autoComplete={autoComplete}
          {...rest}
        />
        <button type="button" className="auth-input-icon-btn" onClick={() => setVisible(v => !v)} aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'} aria-pressed={visible}>
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {capsLock && <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4 }}>Bloq Mayús está activado</div>}
    </>
  );
}

const BRAND_FEATURES = [
  { icon: CalendarCheck, title: 'Planificación de eventos', text: 'Solicitudes, presupuestos y calendario en un solo lugar.' },
  { icon: Ambulance, title: 'Flota y profesionales', text: 'Asignación de vehículos y personal sanitario en tiempo real.' },
  { icon: Lock, title: 'Seguro por diseño', text: 'Datos cifrados, verificación en dos pasos y aislamiento por empresa.' },
];

// Layout compartido de todas las vistas de autenticación: panel de marca a la
// izquierda (oculto en móvil) y columna de contenido a la derecha.
function AuthLayout({ theme, onToggle, maxWidth = 400, children }) {
  return (
    <div className="auth-split">
      <aside className="auth-brand" aria-hidden="true">
        <div>
          <span style={{ display: 'inline-flex', padding: '10px 16px 10px 12px', borderRadius: 14, background: 'rgba(255,255,255,0.94)' }}>
            <BrandLogo size={34} tone="dark" />
          </span>
        </div>
        <div style={{ maxWidth: 440 }}>
          <h2 style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.15, margin: '0 0 12px' }}>Cobertura sanitaria para eventos, sin hojas de cálculo.</h2>
          <p style={{ fontSize: 15, opacity: 0.9, lineHeight: 1.6, margin: '0 0 32px' }}>La plataforma para empresas de ambulancias que gestionan dispositivos preventivos.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {BRAND_FEATURES.map(f => (
              <div key={f.title} className="auth-brand-feature">
                <div className="auth-brand-feature-icon"><f.icon size={18} /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{f.title}</div>
                  <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>{f.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 12, opacity: 0.75 }}>© {new Date().getFullYear()} MedCover Pro</div>
      </aside>

      <main className="auth-main">
        <ThemeToggle theme={theme} onToggle={onToggle} />
        <div className="auth-form-wrap" style={{ maxWidth }}>
          <div className="auth-mobile-logo" style={{ textAlign: 'center', marginBottom: 28 }}>
            <BrandLogo size={40} />
          </div>

          {children}
        </div>
      </main>
    </div>
  );
}

function AuthHeader({ icon: Icon, title, subtitle, tone = 'var(--accent)' }) {
  return (
    <div style={{ marginBottom: 24 }}>
      {Icon && (
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--accent-dim)', color: tone, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <Icon size={24} />
        </div>
      )}
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 6px' }}>{title}</h1>
      {subtitle && <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>{subtitle}</p>}
    </div>
  );
}

function BackLink({ onClick }) {
  return (
    <button type="button" className="auth-link-btn" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 24 }}>
      <ArrowLeft size={16} /> Volver al login
    </button>
  );
}

export default function LoginPage() {
  const { login, loginWithGoogle, complete2FA, completeForced2FA, signupTenant } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [view, setView] = useState(() => {
    // Support ?view=signup_tenant|register|forgot via URL (from LandingPage CTAs)
    const v = new URLSearchParams(window.location.search).get('view');
    const allowed = ['login', 'register', 'signup_tenant', 'forgot'];
    return allowed.includes(v) ? v : 'login';
  }); // login, 2fa, register, registered, forgot, reset_sent, reset_form, signup_tenant
  const [email, setEmail] = useState(readRememberedEmail);
  const [rememberEmail, setRememberEmail] = useState(() => !!readRememberedEmail());
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);
  // Si SA desactiva "Registro público", ocultamos el botón "Crear empresa".
  const [publicSignupAllowed, setPublicSignupAllowed] = useState(true);
  useEffect(() => {
    fetch('/api/public-config').then(r => r.json())
      .then(c => setPublicSignupAllowed(c?.public_signup !== false))
      .catch(() => {});
  }, []);

  // 2FA state
  const [twofaTmpToken, setTwofaTmpToken] = useState('');
  const [twofaCode, setTwofaCode] = useState('');

  // Forced 2FA setup state (admin obligado a configurar 2FA al loguear)
  const [forced2faToken, setForced2faToken] = useState('');
  const [forced2faQr, setForced2faQr] = useState(null); // { secret, qr_data_url }
  const [forced2faCode, setForced2faCode] = useState('');
  const [forced2faBackup, setForced2faBackup] = useState(null);

  // Signup-tenant state (self-serve registration)
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [signup, setSignup] = useState({
    company_name: '', slug: '', cif: '', phone: '',
    admin_first_name: '', admin_last_name: '', admin_email: '', admin_password: '', admin_password2: '',
  });
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null); // 'checking' | 'available' | 'taken' | 'invalid' | null

  // Load demo accounts dynamically
  useEffect(() => {
    fetch('/api/auth/demo-accounts').then(r => r.json()).then(setDemoAccounts).catch(() => {});
  }, []);

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetUrl, setResetUrl] = useState('');

  // Reset password (from token)
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');

  // Check for reset token in URL
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('reset_token');

  // Registration form
  const [reg, setReg] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', password2: '',
    specialization: 'TES', license_number: '', dni: '', tenant_slug: '',
  });
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    writeRememberedEmail(rememberEmail ? email.trim() : '');
    try {
      const result = await login(email.trim(), password);
      if (result?.requires_2fa) {
        setTwofaTmpToken(result.tmp_token);
        setView('2fa');
      } else if (result?.requires_2fa_setup) {
        // Admin obligado a configurar 2FA. Pedimos QR al backend con el
        // setup_token y mostramos vista forced_2fa_setup.
        setForced2faToken(result.setup_token);
        try {
          const qr = await api.twoFAForcedSetup(result.setup_token);
          setForced2faQr(qr);
          setView('forced_2fa_setup');
        } catch (err) {
          setError('No se pudo iniciar el setup de 2FA: ' + (err.message || 'error'));
        }
      }
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  // Debounced slug check
  useEffect(() => {
    if (view !== 'signup_tenant' || !signup.slug) { setSlugStatus(null); return; }
    if (!/^[a-z0-9-]{3,30}$/.test(signup.slug)) { setSlugStatus('invalid'); return; }
    setSlugStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await api.checkSlug(signup.slug);
        setSlugStatus(r.available ? 'available' : 'taken');
      } catch { setSlugStatus(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [signup.slug, view]);

  const autoSlug = (name) => name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);

  const handleSignupTenant = async (e) => {
    e.preventDefault();
    setSignupError('');
    if (!signup.company_name || !signup.slug || !signup.admin_email || !signup.admin_password || !signup.admin_first_name) {
      setSignupError('Completa los campos obligatorios (*)'); return;
    }
    if (signup.admin_password !== signup.admin_password2) { setSignupError('Las contraseñas no coinciden'); return; }
    const pwCheck = validatePassword(signup.admin_password);
    if (!pwCheck.valid) { setSignupError('Contraseña débil: ' + pwCheck.errors.join(', ')); return; }
    const emCheck = validateEmail(signup.admin_email);
    if (!emCheck.valid) { setSignupError(emCheck.error); return; }
    if (slugStatus === 'taken') { setSignupError('El slug ya está en uso'); return; }
    if (slugStatus === 'invalid') { setSignupError('Slug inválido: solo letras minúsculas, números y guiones (3-30 chars)'); return; }
    if (TURNSTILE_ENABLED && !turnstileToken) { setSignupError('Completa la verificación antimbot'); return; }
    setSignupLoading(true);
    try {
      await signupTenant({ ...signup, cf_turnstile_token: turnstileToken });
      // AuthContext setUser dispara navegación
    } catch (err) {
      setSignupError(err.message);
      setTurnstileToken(null); // token is single-use — reset to force a fresh challenge
    }
    setSignupLoading(false);
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError('');
    if (!twofaCode || twofaCode.length < 6) { setError('Introduce el código de 6 dígitos'); return; }
    setLoading(true);
    try {
      await complete2FA(twofaTmpToken, twofaCode.trim());
      // AuthContext setUser triggers App re-render, which navigates away from login
    } catch (err) { setError(err.message); setTwofaCode(''); }
    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const result = await loginWithGoogle(credentialResponse.credential);
      if (result?.requires_2fa) {
        setTwofaTmpToken(result.tmp_token);
        setView('2fa');
      }
    } catch (err) {
      setError(err.message || 'No se pudo iniciar sesión con Google');
    }
    setLoading(false);
  };
  const handleGoogleError = () => { setError('Error con Google Sign-in. Inténtalo de nuevo.'); };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    if (!reg.first_name || !reg.last_name || !reg.email || !reg.password || !reg.tenant_slug) {
      setRegError('Completa todos los campos obligatorios'); return;
    }
    if (reg.password !== reg.password2) { setRegError('Las contraseñas no coinciden'); return; }
    if (reg.password.length < 8) { setRegError('La contraseña debe tener al menos 8 caracteres'); return; }
    setRegLoading(true);
    try {
      await api.registerProfessional(reg);
      setView('registered');
    } catch (err) { setRegError(err.message); }
    setRegLoading(false);
  };

  const fillDemo = (acc) => { setEmail(acc.email); setPassword('password123'); };

  // ─── RESET PASSWORD FORM (from email link with token) ───
  if (resetToken || view === 'reset_form') {
    const token = resetToken || '';
    const backToLogin = () => { window.history.replaceState({}, '', window.location.pathname); setView('login'); };
    return (
      <AuthLayout theme={theme} onToggle={toggleTheme}>
        <BackLink onClick={backToLogin} />
        <AuthHeader icon={KeyRound} title="Nueva contraseña" subtitle="Elige una contraseña segura para tu cuenta." />
        <ErrorBox>{error}</ErrorBox>
        <form onSubmit={async (e) => {
          e.preventDefault();
          setError('');
          const pwCheck = validatePassword(newPw);
          if (!pwCheck.valid) { setError('Contraseña débil: ' + pwCheck.errors.join(', ')); return; }
          if (newPw !== newPw2) { setError('Las contraseñas no coinciden'); return; }
          setLoading(true);
          try {
            const result = await api.resetPassword(token, newPw);
            toast.success('Contraseña actualizada', result.message);
            backToLogin();
          } catch(e) { setError(e.message); }
          setLoading(false);
        }}>
          <FormField label="Nueva contraseña" hint="8+ caracteres, mayúscula, minúscula, número y símbolo">
            <PasswordInput value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" autoFocus maxLength={128} />
          </FormField>
          <FormField label="Confirmar contraseña" error={newPw2 && newPw !== newPw2 ? 'No coincide' : null}>
            <PasswordInput value={newPw2} onChange={e => setNewPw2(e.target.value)} autoComplete="new-password" placeholder="Repite la contraseña" maxLength={128} />
          </FormField>
          <Btn type="submit" icon={KeyRound} disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 12, padding: '12px 16px' }}>
            {loading ? 'Guardando...' : 'Cambiar contraseña'}
          </Btn>
        </form>
      </AuthLayout>
    );
  }

  // ─── FORGOT PASSWORD: EMAIL SENT ───
  if (view === 'reset_sent') {
    return (
      <AuthLayout theme={theme} onToggle={toggleTheme}>
        <AuthHeader icon={Mail} title="Revisa tu correo" subtitle="Si el email existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña. Revisa también la carpeta de spam." />
        {resetUrl && (
          <div style={{ padding: '12px 16px', background: 'var(--surface-alt)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: 6 }}>Enlace de restablecimiento (modo demo)</div>
            <a href={resetUrl} style={{ color: 'var(--accent)', fontSize: 12, wordBreak: 'break-all' }}>{resetUrl}</a>
          </div>
        )}
        <Btn icon={ArrowLeft} onClick={() => { setView('login'); setResetUrl(''); }} style={{ width: '100%', justifyContent: 'center', padding: '12px 16px' }}>Volver al login</Btn>
      </AuthLayout>
    );
  }

  // ─── FORGOT PASSWORD FORM ───
  if (view === 'forgot') {
    return (
      <AuthLayout theme={theme} onToggle={toggleTheme}>
        <BackLink onClick={() => { setView('login'); setError(''); }} />
        <AuthHeader icon={KeyRound} title="¿Olvidaste tu contraseña?" subtitle="Introduce tu email y te enviaremos un enlace para restablecerla." />
        <ErrorBox>{error}</ErrorBox>
        <form onSubmit={async (e) => {
          e.preventDefault();
          setError('');
          if (!forgotEmail.trim()) { setError('Introduce tu email'); return; }
          setLoading(true);
          try {
            const result = await api.forgotPassword(forgotEmail.trim());
            if (result.reset_url) setResetUrl(result.reset_url);
            setView('reset_sent');
          } catch(e) { setError(e.message); }
          setLoading(false);
        }}>
          <FormField label="Email de tu cuenta">
            <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="tu@email.com" autoComplete="email" autoFocus required />
          </FormField>
          <Btn type="submit" icon={Mail} disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 12, padding: '12px 16px' }}>
            {loading ? 'Enviando...' : 'Enviar enlace'}
          </Btn>
        </form>
      </AuthLayout>
    );
  }

  // ─── REGISTERED SUCCESS ───
  if (view === 'registered') {
    return (
      <AuthLayout theme={theme} onToggle={toggleTheme}>
        <AuthHeader icon={CheckCircle} tone="var(--success)" title="Registro completado" subtitle="Tu solicitud ha sido enviada. El administrador de la organización revisará tu perfil y te notificará cuando tu cuenta esté activada." />
        <Btn icon={ArrowLeft} onClick={() => setView('login')} style={{ width: '100%', justifyContent: 'center', padding: '12px 16px' }}>Volver al login</Btn>
      </AuthLayout>
    );
  }

  // ─── REGISTRATION FORM ───
  if (view === 'register') {
    return (
      <AuthLayout theme={theme} onToggle={toggleTheme} maxWidth={520}>
        <BackLink onClick={() => setView('login')} />
        <AuthHeader icon={UserPlus} title="Registro de profesional" subtitle="Completa tus datos. El administrador revisará tu documentación antes de aprobar tu cuenta." />

        <ErrorBox>{regError}</ErrorBox>

        <form onSubmit={handleRegister}>
          <FormField label="Organización (slug)" required hint="El admin de tu empresa te lo facilita">
            <input value={reg.tenant_slug} onChange={e => setReg({...reg, tenant_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})} placeholder="ambulancias-del-sur" maxLength={30} pattern="[a-z0-9-]{3,30}" />
          </FormField>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', marginTop: 12, marginBottom: 8 }}>Datos Personales</div>
          <div className="auth-grid-2">
            <FormField label="Nombre" required>
              <input value={reg.first_name} onChange={e => setReg({...reg, first_name: e.target.value})} maxLength={50} autoComplete="given-name" />
            </FormField>
            <FormField label="Apellidos" required>
              <input value={reg.last_name} onChange={e => setReg({...reg, last_name: e.target.value})} maxLength={80} autoComplete="family-name" />
            </FormField>
            <FormField label="DNI/NIE" required>
              <input value={reg.dni} onChange={e => setReg({...reg, dni: e.target.value.toUpperCase()})} placeholder="12345678A" maxLength={9} pattern="[0-9XYZ][0-9]{7}[A-Z]" />
            </FormField>
            <FormField label="Teléfono" required>
              <input type="tel" value={reg.phone} onChange={e => setReg({...reg, phone: e.target.value})} placeholder="+34 612 345 678" maxLength={20} autoComplete="tel" />
            </FormField>
            <FormField label="Email" required>
              <input type="email" value={reg.email} onChange={e => setReg({...reg, email: e.target.value})} maxLength={254} autoComplete="email" />
            </FormField>
            <FormField label="Especialización" required>
              <select value={reg.specialization} onChange={e => setReg({...reg, specialization: e.target.value})}>
                <option value="TES">TES</option>
                <option value="medico_sva">Médico/a</option>
                <option value="enfermero">Enfermero/a</option>
                <option value="supervisor">Supervisor/a</option>
              </select>
            </FormField>
            <FormField label="Nº Colegiado" hint="Médicos / enfermeros">
              <input value={reg.license_number} onChange={e => setReg({...reg, license_number: e.target.value})} placeholder="280012345" maxLength={20} />
            </FormField>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase', marginTop: 16, marginBottom: 8 }}>Datos de Facturación</div>
          <div className="auth-grid-2">
            <FormField label="IBAN" hint="Para liquidaciones / facturas">
              <input value={reg.iban||''} onChange={e => setReg({...reg, iban: e.target.value.toUpperCase()})} placeholder="ES00 0000 0000 00 0000000000" maxLength={34} style={{ fontFamily: 'var(--font-mono)' }} />
            </FormField>
            <FormField label="Nombre fiscal">
              <input value={reg.fiscal_name||''} onChange={e => setReg({...reg, fiscal_name: e.target.value})} placeholder="Nombre completo o razón social" maxLength={150} />
            </FormField>
            <FormField label="Dirección fiscal" style={{ gridColumn: '1/-1' }}>
              <input value={reg.fiscal_address||''} onChange={e => setReg({...reg, fiscal_address: e.target.value})} maxLength={250} placeholder="Calle, nº, piso, CP, ciudad" autoComplete="street-address" />
            </FormField>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--success)', textTransform: 'uppercase', marginTop: 16, marginBottom: 8 }}>Documentación Requerida</div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Indica qué documentos adjuntas. El administrador los revisará.</p>
          <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
            {[
              { key: 'doc_dni', label: 'DNI / NIE (ambas caras)' },
              { key: 'doc_license', label: 'Carnet de colegiado / Título' },
              { key: 'doc_bank', label: 'Certificado de titularidad bancaria' },
              { key: 'doc_criminal', label: 'Certificado de antecedentes penales' },
              { key: 'doc_minors', label: 'Certificado de antecedentes con menores' },
            ].map(d => (
              <label key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: reg.documents?.[d.key] ? 'rgba(16,185,129,0.06)' : 'var(--surface-alt)', borderRadius: 8, border: `1px solid ${reg.documents?.[d.key] ? 'var(--success)' : 'var(--border)'}`, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={!!reg.documents?.[d.key]} onChange={e => setReg({...reg, documents: {...(reg.documents||{}), [d.key]: e.target.checked}})} style={{ width: 18, height: 18, accentColor: 'var(--success)' }} />
                <span style={{ fontWeight: reg.documents?.[d.key] ? 600 : 400 }}>{d.label}</span>
              </label>
            ))}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--purple)', textTransform: 'uppercase', marginTop: 16, marginBottom: 8 }}>Credenciales de Acceso</div>
          <div className="auth-grid-2">
            <FormField label="Contraseña" required hint="8+ chars, mayús, minús, número, símbolo">
              <input type="password" value={reg.password} onChange={e => setReg({...reg, password: e.target.value})} autoComplete="new-password" minLength={8} maxLength={128} />
            </FormField>
            <FormField label="Confirmar Contraseña" required error={reg.password2 && reg.password !== reg.password2 ? 'No coincide' : null}>
              <input type="password" value={reg.password2} onChange={e => setReg({...reg, password2: e.target.value})} autoComplete="new-password" minLength={8} maxLength={128} />
            </FormField>
          </div>

          <Btn type="submit" icon={UserPlus} disabled={regLoading} style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}>
            {regLoading ? 'Enviando...' : 'Enviar Solicitud de Registro'}
          </Btn>
        </form>
      </AuthLayout>
    );
  }

  // ─── SIGNUP TENANT (nueva empresa) ───
  if (view === 'signup_tenant') {
    const slugMsg = {
      checking: { text: 'Comprobando...', color: 'var(--text-muted)' },
      available: { text: '✓ Disponible', color: 'var(--success)' },
      taken: { text: '✗ Ya en uso', color: 'var(--warn)' },
      invalid: { text: 'Solo letras minúsculas, números y guiones (3-30 chars)', color: 'var(--warn)' },
    }[slugStatus];

    return (
      <AuthLayout theme={theme} onToggle={toggleTheme} maxWidth={520}>
        <BackLink onClick={() => setView('login')} />
        <AuthHeader icon={Building2} title="Crear mi empresa" subtitle="Prueba gratis 14 días, sin tarjeta." />

        <ErrorBox style={{ margin: '16px 0' }}>{signupError}</ErrorBox>

        <form onSubmit={handleSignupTenant}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', marginTop: 16, marginBottom: 10 }}>Datos de la empresa</div>

          <FormField label="Nombre de la empresa" required>
            <input value={signup.company_name} onChange={e => {
              const name = e.target.value;
              setSignup({ ...signup, company_name: name, slug: signup.slug || autoSlug(name) });
            }} placeholder="Ambulancias del Sur" maxLength={120} autoFocus autoComplete="organization" />
          </FormField>

          <FormField
            label="Slug (URL del portal)"
            required
            error={slugStatus === 'taken' ? 'Ya está en uso, prueba otro' : slugStatus === 'invalid' ? 'Solo minúsculas, números y guiones (3-30 caracteres)' : null}
            hint={`Tu portal será: thmedcoverpro.com/access/${signup.slug || 'tu-slug'}`}
          >
            <input value={signup.slug} onChange={e => setSignup({ ...signup, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="ambulancias-del-sur" maxLength={30} pattern="[a-z0-9-]{3,30}" />
            {slugMsg && slugStatus !== 'taken' && slugStatus !== 'invalid' && <div style={{ fontSize: 11, color: slugMsg.color, marginTop: 4 }}>{slugMsg.text}</div>}
          </FormField>

          <div className="auth-grid-2">
            <FormField label="CIF/NIF" hint="Formato: B12345678">
              <input value={signup.cif} onChange={e => setSignup({ ...signup, cif: e.target.value.toUpperCase() })} placeholder="B12345678" maxLength={9} />
            </FormField>
            <FormField label="Teléfono">
              <input type="tel" value={signup.phone} onChange={e => setSignup({ ...signup, phone: e.target.value })} placeholder="+34 600 000 000" maxLength={20} autoComplete="tel" />
            </FormField>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--purple)', textTransform: 'uppercase', marginTop: 16, marginBottom: 10 }}>Tu cuenta (administrador)</div>

          <div className="auth-grid-2">
            <FormField label="Nombre" required>
              <input value={signup.admin_first_name} onChange={e => setSignup({ ...signup, admin_first_name: e.target.value })} maxLength={50} autoComplete="given-name" />
            </FormField>
            <FormField label="Apellidos">
              <input value={signup.admin_last_name} onChange={e => setSignup({ ...signup, admin_last_name: e.target.value })} maxLength={80} autoComplete="family-name" />
            </FormField>
          </div>

          <FormField label="Email" required hint="Será tu usuario para iniciar sesión">
            <input type="email" value={signup.admin_email} onChange={e => setSignup({ ...signup, admin_email: e.target.value })} placeholder="tu@empresa.com" maxLength={254} autoComplete="email" />
          </FormField>

          <div className="auth-grid-2">
            <FormField label="Contraseña" required hint="8+ chars, mayús, minús, número, símbolo">
              <input type="password" value={signup.admin_password} onChange={e => setSignup({ ...signup, admin_password: e.target.value })} autoComplete="new-password" minLength={8} maxLength={128} />
            </FormField>
            <FormField
              label="Confirmar contraseña"
              required
              error={signup.admin_password2 && signup.admin_password !== signup.admin_password2 ? 'No coincide' : null}
            >
              <input type="password" value={signup.admin_password2} onChange={e => setSignup({ ...signup, admin_password2: e.target.value })} autoComplete="new-password" minLength={8} maxLength={128} />
            </FormField>
          </div>

          <Turnstile onToken={setTurnstileToken} action="signup-tenant" theme={theme === 'dark' ? 'dark' : 'light'} />

          <Btn type="submit" icon={Sparkles} disabled={signupLoading || slugStatus === 'taken' || (TURNSTILE_ENABLED && !turnstileToken)} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
            {signupLoading ? 'Creando...' : 'Crear empresa y empezar prueba de 14 días'}
          </Btn>

          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12, textAlign: 'center' }}>
            Al registrarte aceptas los Términos y Política de Privacidad.
          </div>
        </form>
      </AuthLayout>
    );
  }

  // ─── 2FA STEP ───
  if (view === '2fa') {
    const cancel2FA = () => { setView('login'); setTwofaTmpToken(''); setTwofaCode(''); setError(''); setPassword(''); };
    return (
      <AuthLayout theme={theme} onToggle={toggleTheme}>
        <BackLink onClick={cancel2FA} />
        <AuthHeader icon={ShieldCheck} title="Verificación en dos pasos" subtitle="Introduce el código de tu app autenticadora o uno de tus códigos de respaldo." />
        <ErrorBox>{error}</ErrorBox>
        <form onSubmit={handle2FA}>
          <FormField label="Código de verificación">
            <input
              type="text"
              value={twofaCode}
              onChange={e => setTwofaCode(e.target.value)}
              placeholder="123456"
              autoFocus
              autoComplete="one-time-code"
              inputMode="numeric"
              style={{ fontSize: 22, letterSpacing: 6, textAlign: 'center', fontWeight: 700 }}
            />
          </FormField>
          <Btn type="submit" icon={ShieldCheck} disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 12, padding: '12px 16px' }}>
            {loading ? 'Verificando...' : 'Verificar'}
          </Btn>
        </form>
      </AuthLayout>
    );
  }

  // ─── FORCED 2FA SETUP ─── (admin obligado a 2FA pero sin configurar)
  if (view === 'forced_2fa_setup') {
    return (
      <AuthLayout theme={theme} onToggle={toggleTheme} maxWidth={440}>
        <AuthHeader icon={ShieldCheck} title="Configura la verificación en dos pasos" subtitle="Tu organización la exige para todos los administradores. Configúrala ahora para acceder." />

        {forced2faBackup ? (
          // Vista post-éxito: mostrar backup codes antes de redirigir
          <div style={{ background: 'var(--card)', borderRadius: 16, padding: 28, border: '2px solid var(--success)' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <CheckCircle size={48} color="var(--success)" />
              <h3 style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>2FA activado</h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Guarda estos códigos de respaldo en lugar seguro. Cada uno se usa una sola vez si pierdes acceso a tu app autenticadora.</p>
            </div>
            <div style={{ background: 'var(--surface-alt)', padding: 14, borderRadius: 8, fontFamily: 'var(--font-mono)', fontSize: 13, lineHeight: 1.8, letterSpacing: 1 }}>
              {forced2faBackup.map(c => <div key={c}>{c}</div>)}
            </div>
            <Btn icon={LogIn} onClick={() => window.location.reload()} style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}>
              Continuar al panel
            </Btn>
          </div>
        ) : (
          <div>
            <ErrorBox>{error}</ErrorBox>

            <ol style={{ fontSize: 13, color: 'var(--text)', paddingLeft: 20, marginBottom: 16, lineHeight: 1.7 }}>
              <li>Instala una app autenticadora (Google Authenticator, Authy, 1Password, Bitwarden…)</li>
              <li>Escanea este QR con tu app:</li>
            </ol>
            {forced2faQr?.qr_data_url && (
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <img src={forced2faQr.qr_data_url} alt="QR 2FA" style={{ width: 200, height: 200, borderRadius: 8, border: '1px solid var(--border)' }} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                  O introduce manualmente: <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, background: 'var(--surface-alt)', padding: '2px 6px', borderRadius: 4 }}>{forced2faQr?.secret}</code>
                </div>
              </div>
            )}
            <form onSubmit={async (e) => {
              e.preventDefault();
              setError('');
              if (!forced2faCode || forced2faCode.length < 6) { setError('Introduce el código de 6 dígitos'); return; }
              setLoading(true);
              try {
                const data = await completeForced2FA(forced2faToken, forced2faQr.secret, forced2faCode);
                setForced2faBackup(data.backup_codes || []);
              } catch (err) {
                setError(err.message || 'Código inválido');
              } finally {
                // Siempre resetear loading — evita spinner infinito si algo falla raro.
                setLoading(false);
              }
            }}>
              <FormField label="3. Introduce el código que muestra tu app">
                <input
                  type="text"
                  value={forced2faCode}
                  onChange={e => setForced2faCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  style={{ fontSize: 22, letterSpacing: 6, textAlign: 'center', fontWeight: 700 }}
                />
              </FormField>
              <Btn type="submit" icon={ShieldCheck} disabled={loading || forced2faCode.length < 6} style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}>
                {loading ? 'Activando…' : 'Activar 2FA y entrar'}
              </Btn>
            </form>
            <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12 }}>
              <button onClick={() => { setView('login'); setForced2faToken(''); setForced2faQr(null); setForced2faCode(''); setError(''); setPassword(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                Cancelar y volver al login
              </button>
            </div>
          </div>
        )}
      </AuthLayout>
    );
  }

  // ─── LOGIN FORM ───
  return (
    <AuthLayout theme={theme} onToggle={toggleTheme}>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 6px' }}>Bienvenido de nuevo</h1>
      <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 24px' }}>Inicia sesión para acceder a tu panel.</p>

      <ErrorBox>{error}</ErrorBox>

      {GOOGLE_ENABLED && (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme={theme === 'dark' ? 'filled_black' : 'outline'}
              size="large"
              text="signin_with"
              shape="rectangular"
              locale="es"
              useOneTap={false}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.5 }}>o con email</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>
        </>
      )}

      <form onSubmit={handleLogin}>
        <FormField label="Email">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" autoComplete="username" autoFocus={!email} maxLength={254} required />
        </FormField>
        <FormField label="Contraseña">
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoFocus={!!email} maxLength={128} required />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '4px 0 18px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={rememberEmail} onChange={e => setRememberEmail(e.target.checked)} />
            Recordar mi email
          </label>
          <button type="button" className="auth-link-btn" style={{ fontSize: 12 }} onClick={() => { setForgotEmail(email.trim()); setView('forgot'); setError(''); }}>
            ¿Olvidaste tu contraseña?
          </button>
        </div>
        <Btn type="submit" icon={LogIn} disabled={loading || !email || !password} style={{ width: '100%', justifyContent: 'center', padding: '12px 16px' }}>
          {loading ? 'Entrando...' : 'Iniciar sesión'}
        </Btn>
      </form>

      <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {publicSignupAllowed && (
          <button type="button" onClick={() => setView('signup_tenant')} style={{ background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', padding: '12px 14px', borderRadius: 10, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left' }}>
            <span style={{ width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg, var(--accent), var(--purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Building2 size={17} color="#fff" />
            </span>
            <span>
              <span style={{ display: 'block', fontWeight: 700 }}>Crear empresa nueva</span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>Prueba gratis 14 días, sin tarjeta</span>
            </span>
          </button>
        )}
        <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          ¿Eres profesional sanitario?{' '}
          <button type="button" className="auth-link-btn" onClick={() => setView('register')}>Regístrate aquí</button>
        </div>
      </div>

      {/* Demo accounts */}
      {demoAccounts.length > 0 && (
        <div style={{ marginTop: 28, padding: 14, borderRadius: 10, border: '1px dashed var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, textAlign: 'center' }}>Acceso rápido a cuentas demo</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
            {demoAccounts.map(acc => (
              <button type="button" key={acc.email} onClick={() => fillDemo(acc)} title={acc.email} style={{
                padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'var(--card)', border: `1px solid ${acc.color}`, color: acc.color,
              }}>{acc.label}</button>
            ))}
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
